import Vue from 'vue'
import { Dialog as VueDialog } from 'element-ui'

import router from '@plugin/router'
import store from '@plugin/store'
import i18n, { $t } from '@plugin/i18n'
import Form from '@utils/form'
import DialogForm from '@utils/common/operation/components/dialog-form.vue'
import DialogTable from '@utils/common/operation/components/dialog-table.vue'
import Table from '@/utils/table'

const DialogBox = Vue.extend(VueDialog)
const boxes = new Set()

interface Options {
  title?: string;
  component?: any;
  attrs?: { [prop: string]: any };
  [prop: string]: any;
}

class Dialog {
  private box: any
  private resolve: any
  private reject: any

  private _prefix = ''
  private _title = ''
  private _component: any = 'div'
  private _isfooter = true
  private _attrs: { [prop: string]: any } = {}
  public _confirmButtonText = 'common.confirm'
  public _cancelButtonText = 'common.cancel'
  public _validate: (vm: any) => Promise<boolean> = () => Promise.resolve(true)
  public _submit: (vm: any) => Promise<boolean> = () => Promise.resolve(true)
  public _footer: (h: any, vm: any) => any = (h, vm) => [h('span', [
    h('el-button', { key: 'close', on: { click: () => vm.close() }}, [$t(this._dialog.cancelButtonText || this._cancelButtonText)]),
    h('el-button', { key: 'confirm', props: { type: 'primary', loading: vm._loading }, on: { click: () => vm.confirm() }}, [$t(this._dialog.confirmButtonText || this._confirmButtonText)])
  ])]

  public _loading = false

  public get loading(): boolean {
    return this._loading
  }
  public set loading(value) {
    this._loading = value
    const h = this.box.$createElement
    this.box.$slots.footer = this._isfooter ? this._footer(h, this) : []
    this.box.$nextTick(() => {
      this.box.$forceUpdate()
    })
  }

  private _dialog: { [prop: string]: any } = {}

  constructor(prefix = '') {
    this._prefix = prefix
  }

  title(title: string) {
    this._title = this._prefix && title.indexOf('.') === -1 ? `${this._prefix}.${title}` : title
    return this
  }

  confirmButtonText(confirmButtonText: string) {
    this._confirmButtonText = confirmButtonText
    return this
  }
  cancelButtonText(cancelButtonText: string) {
    this._cancelButtonText = cancelButtonText
    return this
  }

  component(component: any) {
    this._component = component
    return this
  }

  isfooter(isfooter: boolean) {
    this._isfooter = isfooter
    return this
  }

  footer(footer: any) {
    this._footer = footer
    return this
  }

  attrs(attrs: { [prop: string]: any }) {
    this._attrs = { ...this._attrs, ...attrs }
    return this
  }

  bind(attr: string, prop: any) {
    if (!attr) return this
    const attrs = { [attr]: prop }
    return this.attrs(attrs)
  }

  dialog(dialog: object) {
    this._dialog = dialog
    return this
  }

  validate(validate: (vm: any) => Promise<boolean>) {
    this._validate = validate
    return this
  }

  submit(submit: (vm: any) => Promise<boolean>) {
    this._submit = submit
    return this
  }

  form(form: Form) {
    return this.component(form.tpl(DialogForm))
      .bind('$form', form)
      .validate(async(vm: any) => {
        const valid = await vm.$refs.form.validate()
        return valid
      })
  }

  table(table: Table, attrs = {}) {
    return this.component(table.tpl(DialogTable))
      .attrs({
        ...attrs,
        $table: table
      })
  }

  private destroy() {
    boxes.delete(this.box)
    this.box.$destroy()
    this.box = null
    this.resolve = null
    this.reject = null
  }

  private close() {
    if (!this.box) return
    this.reject()
  }

  private async confirm() {
    if (!this.box) return
    let valid
    try {
      valid = await this._validate(this.box.$refs.component)
    } catch {
      valid = false
    }
    if (!valid) return
    if (this._submit) {
      try {
        valid = await this._submit(this.box.$refs.component)
      } catch {
        valid = false
      }
    }
    if (valid) {
      this.resolve(this.box.$refs.component)
    }
  }

  open(options: Options = {}) {
    this.box = new DialogBox({ router, store, i18n })
    const box = this.box
    const h = this.box.$createElement
    const { title, component, attrs = {}} = options

    boxes.add(box)
    box.$mount()
    box.appendToBody = true
    box.visible = true
    box.$on('close', () => this.close())
    Object.keys(options).forEach((key: any) => {
      if (!['title', 'component', 'attrs'].includes(key)) {
        box[key] = options[key]
      }
    })
    box.$slots.default = [h(component || this._component, { ref: 'component', props: { ...this._attrs, ...attrs }})]
    box.$slots.footer = this._isfooter
      ? this._footer(h, this) : []
    Object.assign(box, this._dialog)
    box.title = box.$t(title || this._title)
    return new Promise((resolve, reject) => {
      this.resolve = resolve
      this.reject = reject
    }).finally(() => {
      this.destroy()
    })
  }
}

export default Dialog
